import { Controller, useFormContext } from 'react-hook-form';

import type { FormCheckboxProps } from '../types';
import type { FieldValues } from 'react-hook-form';

import { FieldHint } from '@/components/InputLabel';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui/field';

import { cn } from '@/lib/utils';

import { getFormFieldId } from '../FormFieldShell';

export const FormCheckbox = <TFieldValues extends FieldValues = FieldValues>(
  props: FormCheckboxProps<TFieldValues>,
) => {
  const {
    name,
    label,
    description,
    hint,
    isRequired,
    disabled,
    checkboxProps,
  } = props;
  const { control } = useFormContext<TFieldValues>();

  const fieldId = getFormFieldId(String(name));

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field
          data-invalid={fieldState.invalid}
          orientation="horizontal"
          variant="row"
          className="relative items-center"
        >
          <Checkbox
            id={fieldId}
            aria-label={typeof label === 'string' ? label : undefined}
            checked={field.value || false}
            onCheckedChange={(checked) => {
              field.onChange(checked);
            }}
            disabled={disabled}
            aria-invalid={fieldState.invalid}
            {...checkboxProps}
          />
          <FieldContent>
            {label && (
              <div className="flex items-center gap-1">
                <FieldLabel
                  htmlFor={fieldId}
                  className="cursor-pointer text-sm leading-5 font-medium"
                >
                  {label}
                  {isRequired && (
                    <span
                      className={cn(
                        'ml-1',
                        fieldState.invalid
                          ? 'text-destructive-soft-foreground'
                          : 'text-muted-foreground',
                      )}
                    >
                      *
                    </span>
                  )}
                </FieldLabel>
                {hint && <FieldHint hint={hint} />}
              </div>
            )}
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            {description && <FieldDescription>{description}</FieldDescription>}
          </FieldContent>
        </Field>
      )}
    />
  );
};
